import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useCallback } from 'react'

export default function Modal({open, setOpen, onClose, children, header, footer, maxWidth = "lg", zIndex = 50}) {
  const maxWidthClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md", 
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
    "4xl": "sm:max-w-4xl",
    "6xl": "sm:max-w-6xl"
  };

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    setOpen(false);
  }, [onClose, setOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, handleClose]);

  if (!open) return null;

  const hasSlots = header !== undefined || footer !== undefined;

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex }}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300" />
      
      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        {/* Modal panel */}
        <div className={`relative flex flex-col transform rounded-lg bg-white text-left shadow-xl transition-all duration-300 sm:my-8 w-full ${maxWidthClasses[maxWidth]} max-h-[90vh]`}>

          {/* Header slot — always sticky, never scrolls */}
          {hasSlots ? (
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                {header}
              </div>
              <button
                type="button"
                className="ml-3 flex-shrink-0 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          ) : (
            /* Legacy close button when no slots are used */
            <div className="flex-shrink-0 flex justify-end px-4 pt-4">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Body — only this section scrolls */}
          <div className={`flex-1 overflow-y-auto ${hasSlots ? 'px-6 py-4' : 'px-4 pb-4 pt-2 sm:px-6 sm:pb-6'}`}>
            {children}
          </div>

          {/* Footer slot — always sticky, never scrolls */}
          {footer && (
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
